import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useContext } from 'react';
import { alpha, useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { isInternalAppPath, parseHashRouterNavIntent, scheduleScrollToElementAfterRoute, } from '../../utils/internalPath';
import { resolvePublicUrl } from '../../utils/publicUrl';
import { getWvBrandingSafe } from '../../services/wvBrandingConfig';
import { FaqCtaSharedSealContext } from '../LandingFaqCtaBand/FaqCtaBandContext';
import { resolveGradient, sectionBackgrounds } from '../../theme';
/**
 * CTA button that routes by href kind: app paths → RouterLink, `#section` / `/#section` → navigate home
 * then scroll (HashRouter-safe), anything else (mailto:, https:) → plain anchor.
 */
const CtaNavButton = ({ href, variant, sx, children }) => {
    const navigate = useNavigate();
    const intent = href ? parseHashRouterNavIntent(href) : null;
    const routerLink = Boolean(href && isInternalAppPath(href));
    const hashHandled = Boolean(!routerLink && intent && (intent.kind === 'home' || intent.kind === 'app'));
    const onNavClick = useCallback((e) => {
        if (!intent || !(intent.kind === 'home' || intent.kind === 'app'))
            return;
        e.preventDefault();
        if (intent.kind === 'home') {
            navigate({ pathname: '/' }, { replace: false });
            if (intent.scrollToId)
                scheduleScrollToElementAfterRoute(intent.scrollToId);
            return;
        }
        navigate({ pathname: intent.pathname, search: intent.search }, { replace: false });
    }, [navigate, intent]);
    return (_jsx(Button, { ...(href && routerLink
            ? { component: RouterLink, to: href }
            : hashHandled
                ? { type: 'button', onClick: onNavClick }
                : href
                    ? { component: 'a', href }
                    : { type: 'button' }), variant: variant, color: "primary", size: "large", sx: sx, children: children }));
};
const CtaBlock = ({ label, heading, description, buttonLabel, buttonHref, secondaryButtonLabel, secondaryButtonHref, additionalButtons, gradient, sectionBackground: sectionBackgroundOverride, sealWatermark = true, }) => {
    const sealProvidedByFaqCtaBand = useContext(FaqCtaSharedSealContext);
    const showInlineSeal = sealWatermark && !sealProvidedByFaqCtaBand;
    const useLinearGradient = !sealProvidedByFaqCtaBand &&
        sectionBackgroundOverride == null &&
        gradient != null &&
        (typeof gradient === 'string' ||
            (typeof gradient === 'object' && 'from' in gradient && gradient.from && gradient.to));
    const outerBackground = sealProvidedByFaqCtaBand
        ? 'transparent'
        : sectionBackgroundOverride ??
            (useLinearGradient ? resolveGradient(gradient, sectionBackgrounds.light) : sectionBackgrounds.ctaWarmRadial);
    const theme = useTheme();
    const rawSeal = getWvBrandingSafe().wvStateSealOneColorPng;
    const sealSrc = resolvePublicUrl(rawSeal) ?? rawSeal;
    const baseButtonSx = {
        minWidth: { sm: 200 },
        py: 1.25,
        borderRadius: 2,
        fontWeight: 600,
        fontFamily: theme.typography.button.fontFamily,
        fontSize: '1rem',
    };
    const outlinedButtonSx = {
        ...baseButtonSx,
        color: 'primary.main',
        bgcolor: '#ffffff',
        borderWidth: 2,
        borderColor: 'primary.main',
        boxShadow: '0 1px 4px rgba(12, 38, 70, 0.08)',
        '&:hover': {
            bgcolor: (t) => alpha(t.palette.primary.main, 0.06),
            borderColor: 'primary.dark',
            borderWidth: 2,
        },
    };
    const outlinedLinks = [
        ...(secondaryButtonLabel && secondaryButtonHref
            ? [{ label: secondaryButtonLabel, href: secondaryButtonHref }]
            : []),
        ...(additionalButtons ?? []).filter((b) => b?.label && b?.href),
    ];
    return (_jsxs(Box, { component: "section", sx: {
            position: 'relative',
            overflow: 'visible',
            py: { xs: 4, md: 5 },
            px: { xs: 2, sm: 3, md: 4 },
            background: outerBackground,
            textAlign: 'center',
        }, children: [showInlineSeal && sealSrc ? (_jsx(Box, { component: "img", src: sealSrc.replace(/ /g, '%20'), alt: "", "aria-hidden": true, sx: {
                    position: 'absolute',
                    right: { xs: '-16%', md: '-12%' },
                    bottom: 0,
                    top: 'auto',
                    width: { xs: 'min(122vw, 820px)', sm: 'min(108vw, 980px)', md: 'min(102vw, 1280px)' },
                    height: { xs: 'min(88vh, 760px)', sm: 'min(90vh, 840px)', md: 'min(96vh, 1020px)' },
                    maxHeight: 'none',
                    opacity: 0.11,
                    objectFit: 'contain',
                    objectPosition: 'right bottom',
                    pointerEvents: 'none',
                    userSelect: 'none',
                    zIndex: 0,
                } })) : null, _jsxs(Box, { sx: {
                    position: 'relative',
                    zIndex: 1,
                    /** Wide enough for three 200px buttons in one row; description keeps its own 520px measure. */
                    maxWidth: 760,
                    mx: 'auto',
                    ...(sealProvidedByFaqCtaBand
                        ? {
                            /* Same continuous cream band as FAQ — no white card */
                            bgcolor: 'transparent',
                            borderRadius: 0,
                            px: { xs: 2, sm: 2.5 },
                            py: { xs: 2.5, md: 3 },
                            boxShadow: 'none',
                        }
                        : {}),
                }, children: [label && (_jsx(Typography, { variant: "overline", sx: { display: 'block', mb: 1, color: 'text.secondary', letterSpacing: 1 }, children: label })), _jsx(Typography, { variant: "h4", component: "h2", sx: {
                            fontFamily: (t) => t.typography.h4.fontFamily,
                            fontWeight: 700,
                            color: 'primary.dark',
                            mb: 1.5,
                            letterSpacing: '-0.02em',
                            lineHeight: 1.25,
                            fontSize: { xs: '1.5rem', md: '1.75rem' },
                        }, children: heading }), description && (_jsx(Typography, { variant: "body1", sx: {
                            mb: 2.5,
                            color: 'text.secondary',
                            lineHeight: 1.65,
                            maxWidth: 520,
                            mx: 'auto',
                            fontFamily: (t) => t.typography.body1.fontFamily,
                            fontWeight: 400,
                            fontSize: '1rem',
                        }, children: description })), _jsxs(Stack, { direction: { xs: 'column', sm: 'row' }, spacing: 2, useFlexGap: true, flexWrap: "wrap", justifyContent: "center", alignItems: "center", sx: { mt: 1 }, children: [_jsx(CtaNavButton, { href: buttonHref, variant: "contained", sx: baseButtonSx, children: buttonLabel }), outlinedLinks.map((link) => (_jsx(CtaNavButton, { href: link.href, variant: "outlined", sx: outlinedButtonSx, children: link.label }, `${link.label}-${link.href}`)))] })] })] }));
};
export default CtaBlock;
